import * as echarts from 'echarts/core';
import StaticEChart from '@/components/common/StaticEChart';
import type { EChartsOption } from 'echarts';
import type { StaticEChartHandle } from '@/components/common/StaticEChart';
import { breakpointsMediaQuery, getColor, useMediaQuery } from '@/helpers/utils';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import numberHumanizer from '@/helpers/numberHumanizer';
import timeStampToDate from '@/helpers/timeStampToDate';
import { useRef } from 'react';
import { useTooltipHide } from '@/hooks/useTooltipHide';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  CanvasRenderer,
  LegendComponent
]);

type SearchVolumeSeries = {
  name: string;
  data: unknown[];
  itemStyle?: Record<string, unknown>;
};

type SearchVolumeChartProps = {
  series: SearchVolumeSeries[];
  dates: Array<string | number>;
  count: number;
  isFirstRow?: boolean;
  isLastRow?: boolean;
};

type VolumeTooltipParam = {
  name?: string | number;
  marker?: string;
  value?: string | number | null;
};

const SearchVolumeChart = ({
  series,
  dates,
  count
}: SearchVolumeChartProps) => {
  const chartRef = useRef<StaticEChartHandle | null>(null);
  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  useTooltipHide(chartRef);

  const chart_series = series.slice(count * -1).map(item => {
    return {
      animation: false,
      type: 'bar',
      name: item.name,
      data: item.data,
      stack: 'one',
      itemStyle: item.itemStyle,
      showSymbol: false,
      symbol: 'circle',
      smooth: false,
      emphasis: {
        itemStyle: {
          color: getColor('navbar-vertical-default-link-active-color')
        },
        lineStyle: {
          color: getColor('navbar-vertical-default-link-active-color')
        }
      }
    };
  });

  /*کانفیگ های مربوط به نمودار */
  const options = ({
    textStyle: {
      fontFamily: 'dana'
    },

    tooltip: {
      triggerOn: isMdUp ? 'mousemove' : 'click',
      trigger: 'axis',
      padding: [7, 10],
      appendTo: document.body,
      backgroundColor: getColor('gray-100'),
      borderColor: getColor('gray-300'),
      textStyle: { color: getColor('gray-1100') },
      borderWidth: 1,
      z: 9999999,
      formatter: (params: VolumeTooltipParam[]) => {
        return `
          <div>
          <div style="font-family: dana !important;gap:5px">
          <div class="d-flex gap-1 fs--2">
        تاریخ جستجو <span style="direction:rtl !important;">${timeStampToDate(Number(params[0].name)).slice(8).slice(0, -3).toPersianDigit()}:</span>
          </div>  
          <div style="direction:rtl;" class="d-flex flex-column">
          <span class="d-flex justify-content-between" style="direction:ltr;margin:0;padding:0"><span class="fw-bolder">${params[0].marker} ${numberHumanizer(params[0].value).toString().toPersianDigit()}</span>
            </div>
          </div>`;
      },
      transitionDuration: 0,
      axisPointer: {
        type: 'shadow'
      }
    },
    xAxis: {
      type: 'category',
      data: dates.slice(count * -1),
      axisLine: {
        show: false,
        lineStyle: {
          color: getColor('gray-300'),
          type: 'solid'
        }
      },
      axisTick: { show: false },
      axisLabel: { show: false },
      splitLine: {
        show: false
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        show: false,
        color: getColor('gray-800'),
        margin: 5
      },
      splitLine: {
        show: false,
        lineStyle: {
          color: getColor('gray-200')
        }
      },
      axisTick: { show: false },
      axisLine: { show: false },
      min: 0
    },
    series: chart_series,
    grid: { right: '0%', left: '0%', bottom: '20%', top: '20%' }
  } as unknown) as EChartsOption;

  return (
    <StaticEChart
      echarts={echarts}
      option={options}
      ref={chartRef}
      style={{ height: '100%' }}
    />
  );
};
export default SearchVolumeChart;
